'use client'
import { useEffect } from 'react'
import { useAtom, atom } from 'jotai'
import { atomWithReset } from 'jotai/utils'

import { useFaro } from '@docpace/shared-react-hooks' 
import { ctxPracticeHeaderImageUrlAtom, ctxPracticeIdAtom, ctxPracticeNameAtom } from '@docpace/shared-react-atoms'
import { delayPromise } from '@docpace/shared-ts-utils'

interface UseServerInformationProps {
    serverHostname: string
}

export interface UseServerInformationOutput {
    isFetching: boolean
    serverError: string | undefined
    practiceId: string | undefined
    practiceName: string | undefined
    practiceHeaderImageUrl: string | undefined
    serverHostname: string
}

const _hasBegunInitializingAtom = atom<boolean>(false) // don't use outside of this hook
const _isFetchingAtom = atom<boolean>(true) // don't use outside of this hook
const _serverErrorAtom = atomWithReset<string | undefined>(undefined) // don't use outside of this hook

export function useServerInformation(
    { serverHostname }: UseServerInformationProps
): UseServerInformationOutput {
    const [ practiceId, setCtxPracticeId ] = useAtom(ctxPracticeIdAtom)
    const [ practiceName, setCtxPracticeName ] = useAtom(ctxPracticeNameAtom)
    const [ practiceHeaderImageUrl, setCtxPracticeHeaderImageUrl ] = useAtom(ctxPracticeHeaderImageUrlAtom)
    const { pushFaroError } = useFaro()
    const [ hasBegunInitializing, setHasBegunInitializing ] = useAtom(_hasBegunInitializingAtom) 
    const [ isFetching, setIsFetching ] = useAtom(_isFetchingAtom) 
    const [ serverError, setServerError ] = useAtom(_serverErrorAtom)

    
    useEffect(() => {
        if(!practiceId || !hasBegunInitializing){
            setIsFetching(true)
            delayPromise(100).then(() => { setHasBegunInitializing(true) }) // delay switching this atom a bit to ensure there's no UI flash
            try{
                fetch(`//${serverHostname}/api/server`, {
                    method: 'get',
                    headers: { 'Content-Type': 'application/json' },
                })
                    .then((r) => r.json())
                    .then(({ practiceId, practiceName, practiceHeaderImageUrl }) => {

                        setCtxPracticeId(practiceId)
                        setCtxPracticeName(practiceName)
                        setCtxPracticeHeaderImageUrl(practiceHeaderImageUrl)
                        if(typeof window !== 'undefined'){
                            localStorage.setItem('practiceId', practiceId)
                            localStorage.setItem('practiceName', practiceName)
                            localStorage.setItem('practiceHeaderImageUrl', practiceHeaderImageUrl)
                        }
                    }).finally(()=>{
                        setIsFetching(false)
                    })
            } catch(e: any){
                setServerError(e)
                pushFaroError(e)
                setIsFetching(false)
            }
        }
    }, [])

    return {
        serverError: (serverError && serverError?.length > 0) ? serverError : undefined,
        isFetching: isFetching || !hasBegunInitializing, // this maintains compatibility with the existing error message components
        practiceId,
        practiceName,
        practiceHeaderImageUrl,
            // const headerImageUrl = 'static/hendrick-color-logo.svg'
        serverHostname,
    }
}

export default useServerInformation
